import { AlertCircle, BarChart3, Plus, RefreshCw, Trash2 } from "lucide-react";
import { QueryResultChart } from "./QueryResultChart";
import { QueryResultTable } from "./QueryResultTable";
import type {
  Pinboard,
  PinboardPanel,
  PinboardPanelResult,
} from "../lib/pinboards";
import type { QuerygenLegacyTablePayload } from "../types";

type PinboardWorkspaceProps = {
  pinboards: Pinboard[];
  activePinboardId: string | null;
  onActivePinboardChange: (pinboardId: string) => void;
  onCreatePinboard: () => void;
  onDeletePanel: (pinboardId: string, panelId: string) => void;
  onRefreshPanel: (pinboardId: string, panelId: string) => void;
  onRefreshPinboard: (pinboardId: string) => void;
  refreshingPanelIds: Set<string>;
  panelResults: Record<string, PinboardPanelResult>;
};

const toTablePayload = (
  result: PinboardPanelResult | undefined,
): QuerygenLegacyTablePayload => {
  const rows = result?.rows ?? [];
  const headers = Array.from(
    rows.reduce<Set<string>>((keys, row) => {
      Object.keys(row).forEach((key) => keys.add(key));
      return keys;
    }, new Set<string>()),
  );

  return {
    type: "table",
    headers,
    rows: rows.map((row) => headers.map((header) => row[header] ?? null)),
    pagination: null,
  };
};

const formatPanelTimestamp = (timestamp: string): string =>
  new Date(timestamp).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export function PinboardWorkspace({
  pinboards,
  activePinboardId,
  onActivePinboardChange,
  onCreatePinboard,
  onDeletePanel,
  onRefreshPanel,
  onRefreshPinboard,
  refreshingPanelIds,
  panelResults,
}: PinboardWorkspaceProps) {
  const activePinboard =
    pinboards.find((pinboard) => pinboard.id === activePinboardId) ??
    pinboards[0] ??
    null;

  return (
    <div className="thin-scrollbar h-full overflow-y-auto bg-[#f8fafc]">
      <div className="mx-auto flex min-h-full max-w-[1280px] flex-col px-5 py-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 pb-4">
          <div className="min-w-0">
            <div className="querygen-mono text-[10px] uppercase tracking-[0.2em] text-[#87909d]">
              Pinboards
            </div>
            <h1 className="mt-1 truncate text-[22px] font-semibold tracking-[-0.02em] text-[#111827]">
              {activePinboard?.name ?? "No pinboard yet"}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {pinboards.length ? (
              <select
                value={activePinboard?.id ?? ""}
                onChange={(event) => onActivePinboardChange(event.target.value)}
                className="h-9 min-w-[190px] rounded-md border border-slate-200 bg-white px-3 text-[13px] text-slate-700 outline-none focus:border-slate-400"
              >
                {pinboards.map((pinboard) => (
                  <option key={pinboard.id} value={pinboard.id}>
                    {pinboard.name}
                  </option>
                ))}
              </select>
            ) : null}
            <button
              type="button"
              onClick={onCreatePinboard}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-[#111827] px-3 text-[13px] font-medium text-white transition-colors hover:bg-black"
            >
              <Plus className="h-4 w-4" />
              New pinboard
            </button>
            {activePinboard ? (
              <button
                type="button"
                onClick={() => onRefreshPinboard(activePinboard.id)}
                disabled={
                  activePinboard.panels.length === 0 ||
                  activePinboard.panels.some((panel) =>
                    refreshingPanelIds.has(panel.id),
                  )
                }
                className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-[13px] font-medium text-slate-700 transition-colors hover:text-slate-950 disabled:cursor-not-allowed disabled:text-slate-400"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh all
              </button>
            ) : null}
          </div>
        </div>

        {!activePinboard ? (
          <div className="flex flex-1 items-center justify-center py-20">
            <div className="max-w-[420px] text-center">
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500">
                <BarChart3 className="h-5 w-5" />
              </div>
              <div className="mt-4 text-[15px] font-semibold text-slate-900">
                Create a pinboard
              </div>
              <p className="mt-2 text-[13px] leading-6 text-slate-600">
                Pin chart or table results from chat to assemble a dashboard.
              </p>
            </div>
          </div>
        ) : activePinboard.panels.length === 0 ? (
          <div className="flex flex-1 items-center justify-center py-20">
            <div className="max-w-[440px] text-center">
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500">
                <BarChart3 className="h-5 w-5" />
              </div>
              <div className="mt-4 text-[15px] font-semibold text-slate-900">
                No panels yet
              </div>
              <p className="mt-2 text-[13px] leading-6 text-slate-600">
                Run a query, then use Pin on the result to add a table or chart panel here.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid auto-rows-max grid-cols-1 gap-4 py-5 lg:grid-cols-2">
            {activePinboard.panels.map((panel) => {
              const result = panelResults[panel.id];

              return (
                <section
                  key={panel.id}
                  className="min-w-0 rounded-md border border-slate-200 bg-white shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
                    <div className="min-w-0">
                      <div className="truncate text-[14px] font-semibold text-slate-950">
                        {panel.title}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                        <span className="querygen-mono uppercase tracking-[0.14em]">
                          {panel.kind}
                        </span>
                        <span>
                          {(result?.rowCount ?? 0).toLocaleString()} rows
                          {result?.truncated ? " (truncated)" : ""}
                        </span>
                        <span>
                          {result?.refreshedAt
                            ? `Refreshed ${formatPanelTimestamp(result.refreshedAt)}`
                            : `Pinned ${formatPanelTimestamp(panel.createdAt)}`}
                        </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => onRefreshPanel(activePinboard.id, panel.id)}
                        disabled={refreshingPanelIds.has(panel.id) || !panel.query}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:text-slate-300"
                        title="Refresh panel"
                      >
                        <RefreshCw
                          className={`h-4 w-4 ${
                            refreshingPanelIds.has(panel.id) ? "animate-spin" : ""
                          }`}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeletePanel(activePinboard.id, panel.id)}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                        title="Delete panel"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  {panel.query ? (
                    <div className="border-b border-slate-100 bg-slate-50 px-4 py-2">
                      <code className="line-clamp-2 text-[11px] leading-5 text-slate-600">
                        {panel.query}
                      </code>
                    </div>
                  ) : null}
                  {result?.error ? (
                    <div className="flex items-start gap-2 border-b border-red-100 bg-red-50 px-4 py-3 text-[12px] leading-5 text-red-700">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{result.error}</span>
                    </div>
                  ) : null}
                  <div className="px-4 pb-4">
                    {panel.kind === "chart" && panel.chartConfig && result ? (
                      <QueryResultChart
                        data={result.rows}
                        chartConfig={panel.chartConfig}
                        downloadFilename={`${panel.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${panel.id}.png`}
                      />
                    ) : (
                      <QueryResultTable payload={toTablePayload(result)} />
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
